import * as PIXI from 'pixi.js';
import { GifSprite } from 'pixi.js/gif';
import type { InputState } from '../systems';
import type { ICollisionObject } from '@/types/CollisionTypes';
import { Collider } from './Collider';
import Hidash from "@/utils/Hidash";

const animationFemale = {
  // Default idle animation (fallback)
  idle: '/assets/player/idle.gif',
  // Direction-specific idle animations
  idle_N: '/assets/player/idle_N.gif',
  idle_NE: '/assets/player/idle_NE.gif',
  idle_E: '/assets/player/idle_E.gif',
  idle_SE: '/assets/player/idle_SE.gif',
  idle_S: '/assets/player/idle_S.gif',
  idle_SW: '/assets/player/idle_SW.gif',
  idle_W: '/assets/player/idle_W.gif',
  idle_NW: '/assets/player/idle_NW.gif',
  // Walking animations
  walk_N: '/assets/player/walk_N.gif',
  walk_NE: '/assets/player/walk_NE.gif',
  walk_E: '/assets/player/walk_E.gif',
  walk_SE: '/assets/player/walk_SE.gif',
  walk_S: '/assets/player/walk_S.gif',
  walk_SW: '/assets/player/walk_SW.gif',
  walk_W: '/assets/player/walk_W.gif',
  walk_NW: '/assets/player/walk_NW.gif'
};
const animationMaleV2 = {
  // Default idle animation (fallback)
  idle: '/assets/player/male_v2/idle.gif',
  // Direction-specific idle animations
  idle_N: '/assets/player/male_v2/idle_N.gif',
  idle_NE: '/assets/player/male_v2/idle_NE.gif',
  idle_E: '/assets/player/male_v2/idle_E.gif',
  idle_SE: '/assets/player/male_v2/idle_SE.gif',
  idle_S: '/assets/player/male_v2/idle_S.gif',
  idle_SW: '/assets/player/male_v2/idle_SW.gif',
  idle_W: '/assets/player/male_v2/idle_W.gif',
  idle_NW: '/assets/player/male_v2/idle_NW.gif',
  // Walking animations
  walk_N: '/assets/player/male_v2/walk_N.gif',
  walk_NE: '/assets/player/male_v2/walk_NE.gif',
  walk_E: '/assets/player/male_v2/walk_E.gif',
  walk_SE: '/assets/player/male_v2/walk_SE.gif',
  walk_S: '/assets/player/male_v2/walk_S.gif',
  walk_SW: '/assets/player/male_v2/walk_SW.gif',
  walk_W: '/assets/player/male_v2/walk_W.gif',
  walk_NW: '/assets/player/male_v2/walk_NW.gif'
};

export class Player {
  public sprite: PIXI.Sprite | GifSprite;
  private speed = 200; // pixels per second
  private sprintMultiplier = 1.6; // Sprint speed multiplier
  private x: number;
  private y: number;
  private playerSize = 30; // Size of the player for collision detection
  private animations: Map<string, GifSprite> = new Map();
  private currentAnimation = '';  // Start empty so idle animation gets set initially
  private currentDirection = 'S'; // Current facing direction
  private collider: Collider; // Visual collider for debugging

  private selectedGender = "male"

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;

    // Initialize with a temporary sprite - will be replaced when animations load
    // Use PIXI.Assets.load to create proper GIF source first, but use a fallback for initial state
    this.sprite = new PIXI.Sprite();
    this.sprite.anchor.set(0.5);
    this.sprite.x = this.x;
    this.sprite.y = this.y;
    this.sprite.zIndex = 2; // Player should be on top of NPCs (NPCs have zIndex 1)
    
    // Create visual collider for debugging (hidden by default)
    // Position at feet level (bottom of sprite) instead of centered
    this.collider = new Collider({
      x: this.x - this.playerSize / 2,
      y: this.y,  // At feet level (bottom of sprite)
      width: this.playerSize,
      height: this.playerSize,
      color: 0x00ff00, // Green color for player collider
      visible: false,
    });
    
    // Load animations
    this.loadAnimations();
  }

  private async loadAnimations(): Promise<void> {

    let gender = Hidash.getSearchParams('gender')

    const animationPaths = gender === "male" ? animationMaleV2 : animationFemale

    // Load all animation GIFs using proper PIXI.Assets.load() first
    for (const [key, path] of Object.entries(animationPaths)) {
      try {
        // Load the GIF asset first using PIXI.Assets.load()
        const gifSource = await PIXI.Assets.load(path);
        
        // Create GifSprite with loaded source using proper constructor
        const gifSprite = new GifSprite(gifSource);
        
        // Set up sprite properties
        gifSprite.anchor.set(0.5);
        gifSprite.loop = true;
        gifSprite.animationSpeed = 1;
        
        // Start animation
        gifSprite.play();
        
        this.animations.set(key, gifSprite);
        console.log(`Loaded GIF animation: ${key}`);
      } catch (error) {
        console.warn(`Failed to load GIF animation: ${path}`, error);
        // Create a simple fallback sprite with empty texture
        const fallbackSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
        fallbackSprite.anchor.set(0.5);
        // Store as regular sprite, but cast for type compatibility
        this.animations.set(key, fallbackSprite as unknown as GifSprite);
      }
    }

    // Set initial animation based on current direction
    // Default to South-facing idle if it's the player's first spawn
    this.setAnimation(`idle_${this.currentDirection}`);
    
    // If idle animation wasn't loaded (fallback case), at least show something
    if (this.sprite instanceof PIXI.Sprite && this.sprite.texture === PIXI.Texture.EMPTY) {
      console.warn('Direction-specific idle animation not loaded, player will appear as empty sprite until moving');
    }
  }

  private setAnimation(animationKey: string): void {
    // If the requested animation doesn't exist, try fallback options
    let finalAnimationKey = animationKey;
    
    if (!this.animations.has(animationKey)) {
      // If trying to use a directional idle but it's not available
      if (animationKey.startsWith('idle_')) {
        // Fallback to default idle
        console.warn(`Directional idle animation ${animationKey} not found, using default idle`);
        finalAnimationKey = 'idle';
      }
    }
    
    if (this.animations.has(finalAnimationKey) && this.currentAnimation !== finalAnimationKey) {
      // Stop current animation if it's a GifSprite
      if (this.sprite instanceof GifSprite) {
        this.sprite.stop();
      }
      
      // Get new animation
      const newAnimation = this.animations.get(finalAnimationKey);
      if (!newAnimation) return;
      
      // Copy position and properties
      newAnimation.x = this.x;
      newAnimation.y = this.y;
      newAnimation.zIndex = 2; // Ensure player stays on top of NPCs
      
      // Replace sprite
      if (this.sprite.parent) {
        this.sprite.parent.addChild(newAnimation);
        this.sprite.parent.removeChild(this.sprite);
      }
      
      this.sprite = newAnimation;
      this.currentAnimation = finalAnimationKey;
      
      // Start new animation
      if (newAnimation instanceof GifSprite) {
        newAnimation.play();
      }
    }
  }

  public update(
    deltaTime: number,
    input: InputState,
    worldBounds: PIXI.Rectangle,
    colliders: ICollisionObject[] = [],
  ): void {

    let paramGender = Hidash.getSearchParams('gender')

    // console.log(`QP GENDER`, paramGender);

    if(paramGender && paramGender!==this.selectedGender){
      this.loadAnimations()
      this.selectedGender = paramGender
    }

    // Calculate current speed with sprint multiplier
    const currentSpeed = input.sprint ? this.speed * this.sprintMultiplier : this.speed;
    const moveDistance = currentSpeed * deltaTime;

    // Convert input to 8-directional movement
    let deltaX = 0;
    let deltaY = 0;

    // Only move if there's significant input (dead zone threshold)
    const deadZone = 0.1;
    if (Math.abs(input.horizontalAxis) > deadZone || Math.abs(input.verticalAxis) > deadZone) {
      // Determine the dominant direction based on input
      const angle = Math.atan2(input.verticalAxis, input.horizontalAxis);
      
      // Convert angle to one of 8 directions (45-degree increments)
      // Add π/8 (22.5°) to center the angle ranges around each direction
      const adjustedAngle = angle + Math.PI / 8;
      
      // Normalize to 0-2π range
      const normalizedAngle = adjustedAngle < 0 ? adjustedAngle + 2 * Math.PI : adjustedAngle;
      
      // Convert to direction index (0-7)
      const directionIndex = Math.floor(normalizedAngle / (Math.PI / 4)) % 8;
      
      // Map to 8-directional unit vectors
      // atan2 measures counter-clockwise from positive X axis
      // In screen coordinates: +X = right, +Y = down
      const directions = [
        { x: 1, y: 0, name: 'E' },      // East (right)
        { x: 1, y: 1, name: 'SE' },     // Southeast (down-right)
        { x: 0, y: 1, name: 'S' },      // South (down)
        { x: -1, y: 1, name: 'SW' },    // Southwest (down-left)
        { x: -1, y: 0, name: 'W' },     // West (left)
        { x: -1, y: -1, name: 'NW' },   // Northwest (up-left)
        { x: 0, y: -1, name: 'N' },     // North (up)
        { x: 1, y: -1, name: 'NE' }     // Northeast (up-right)
      ];
      
      const direction = directions[directionIndex];
      
      // Update current direction for animation
      this.currentDirection = direction.name;
      
      // Set walking animation based on direction
      this.setAnimation(`walk_${direction.name}`);
      
      // Apply movement with proper normalization for diagonal movement
      const normalizer = Math.abs(direction.x) > 0 && Math.abs(direction.y) > 0 ? 1 / Math.sqrt(2) : 1;
      deltaX = direction.x * moveDistance * normalizer;
      deltaY = direction.y * moveDistance * normalizer;
    } else {
      // No movement - set idle animation based on the last direction
      this.setAnimation(`idle_${this.currentDirection}`);
    }

    // Calculate new position
    const newX = this.x + deltaX;
    const newY = this.y + deltaY;

    // Check collision with colliders - test X and Y movement separately for sliding
    let finalX = this.x;
    let finalY = this.y;

    // Test horizontal movement (collider at feet level)
    const testXBounds = new PIXI.Rectangle(
      newX - this.playerSize / 2,
      this.y,  // Feet level
      this.playerSize,
      this.playerSize,
    );

    let canMoveX = true;
    for (const collider of colliders) {
      if (collider.intersects(testXBounds)) {
        canMoveX = false;
        break;
      }
    }

    if (canMoveX) {
      finalX = newX;
    }

    // Test vertical movement (collider at feet level)
    const testYBounds = new PIXI.Rectangle(
      finalX - this.playerSize / 2,
      newY,  // Feet level
      this.playerSize,
      this.playerSize,
    );

    let canMoveY = true;
    for (const collider of colliders) {
      if (collider.intersects(testYBounds)) {
        canMoveY = false;
        break;
      }
    }

    if (canMoveY) {
      finalY = newY;
    }

    // Apply world boundary checking
    finalX = Math.max(
      worldBounds.x + this.playerSize / 2,
      Math.min(worldBounds.x + worldBounds.width - this.playerSize / 2, finalX),
    );
    finalY = Math.max(
      worldBounds.y + this.playerSize / 2,
      Math.min(worldBounds.y + worldBounds.height - this.playerSize / 2, finalY),
    );

    // Update position
    this.x = finalX;
    this.y = finalY;
    this.sprite.x = this.x;
    this.sprite.y = this.y;
    
    // Update collider position (at feet level)
    this.collider.setPosition(this.x - this.playerSize / 2, this.y);

    // No need for rotation since we have directional sprites
  }

  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  public setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.sprite.x = x;
    this.sprite.y = y;
    // Update collider position when teleporting (at feet level)
    this.collider.setPosition(this.x - this.playerSize / 2, this.y);
  }

  /**
   * Get the player's collider sprite for adding to the scene
   */
  public getColliderSprite(): PIXI.Graphics {
    return this.collider.sprite;
  }

  /**
   * Get the player's collider for collision detection
   */
  public getCollider(): Collider {
    return this.collider;
  }

  /**
   * Set the visibility of the player's collider (for debugging)
   */
  public setColliderVisible(visible: boolean): void {
    this.collider.setVisible(visible);
  }

  public getCurrentDirection(): string {
    return this.currentDirection;
  }

  // Set the player's direction 
  public setDirection(direction: string): void {
      const validDirections = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      if (!validDirections.includes(direction)) {
          console.warn(`Invalid direction set: ${direction}. Must be one of: ${validDirections.join(', ')}`);
          return;
      }
      this.currentDirection = direction;
      this.setAnimation(`idle_${direction}`);
  }
}
